import { computed, onMounted, onUnmounted, ref } from 'vue'

export function usePrefersReducedMotion () {
  const reduced = ref(false)

  function sync () {
    reduced.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  onMounted(() => {
    sync()
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = () => sync()
    mq.addEventListener?.('change', handler)
    onUnmounted(() => mq.removeEventListener?.('change', handler))
  })

  return { reduced }
}

/** Animate a numeric display string (supports currency / commas / plain ints). */
export function useCountUp (source, { duration = 900 } = {}) {
  const display = ref(typeof source === 'function' ? source() : source?.value ?? source)
  const { reduced } = usePrefersReducedMotion()
  let raf = 0

  function parse (raw) {
    const str = String(raw ?? '')
    const prefix = str.match(/^[^\d-]*/)?.[0] || ''
    const suffix = str.match(/[^\d.,]*$/)?.[0] || ''
    const num = Number(str.replace(/[^0-9.-]/g, ''))
    const decimals = (str.split('.')[1] || '').replace(/\D/g, '').length
    return { prefix, suffix, num: Number.isFinite(num) ? num : 0, decimals, raw: str }
  }

  function format (n, meta) {
    if (!Number.isFinite(n)) return meta.raw
    const fixed = meta.decimals > 0 ? n.toFixed(meta.decimals) : String(Math.round(n))
    const [whole, frac] = fixed.split('.')
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return `${meta.prefix}${withCommas}${frac != null ? `.${frac}` : ''}${meta.suffix}`
  }

  function run (toValue) {
    cancelAnimationFrame(raf)
    const meta = parse(toValue)
    if (reduced.value || meta.num === 0) {
      display.value = meta.raw
      return
    }
    const start = performance.now()
    const from = 0
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - (1 - t) ** 3
      display.value = format(from + (meta.num - from) * eased, meta)
      if (t < 1) raf = requestAnimationFrame(tick)
      else display.value = meta.raw
    }
    raf = requestAnimationFrame(tick)
  }

  onMounted(() => {
    const initial = typeof source === 'function' ? source() : source?.value ?? source
    run(initial)
  })

  onUnmounted(() => cancelAnimationFrame(raf))

  return { display, rerun: run }
}

export function useMotionPresets () {
  const { reduced } = usePrefersReducedMotion()

  const fadeUp = computed(() => {
    if (reduced.value) return { initial: { opacity: 1, y: 0 }, enter: { opacity: 1, y: 0 } }
    return {
      initial: { opacity: 0, y: 14 },
      enter: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', stiffness: 120, damping: 20 }
      }
    }
  })

  function stagger (index, baseDelay = 55) {
    if (reduced.value) return fadeUp.value
    return {
      initial: { opacity: 0, y: 16 },
      enter: {
        opacity: 1,
        y: 0,
        transition: {
          type: 'spring',
          stiffness: 130,
          damping: 22,
          delay: index * baseDelay
        }
      }
    }
  }

  return { reduced, fadeUp, stagger }
}
